import classNames from 'classnames';
import {
  ReactElement,
  MouseEvent,
  useCallback,
  useRef,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  IError,
  redirectWhiteList,
  useQueryParams,
  Vendors,
  useToast,
  ChevronLeftIcon,
} from '@comptia-sso/core';
import { useHostedRouterSync } from '../../../views/SierraView/host';
import { useSierra } from '../../SierraContext';
import { Link as ScrollLink, LinkProps } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { PopupMenu } from '../PopupMenu/PopupMenu';
import { PopupMenuItem } from '../PopupMenu/PopupMenuItem';

// Hooks.
import { useTrackEvent, useProfile, useRedirectToVendor } from 'hooks';

// Styles.
import styles from '../Header.module.scss';
import { env } from 'config/env';

const navOffset = -70;
const scrollDuration = 1000;

interface INavLinkProps extends Omit<LinkProps, 'onClick'> {
  isActive?: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
}

const NavLink = (props: INavLinkProps): ReactElement => {
  const { children, isActive, onClick, onSetActive, to, href } = props;
  const trackEvent = useTrackEvent('Header');
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = useCallback(
    (e, href) => {
      trackEvent('Clicked', { label: e.target.innerText });
      if (
        !location.pathname.toLowerCase().startsWith(href ?? ''.toLowerCase())
      ) {
        navigate(href, { replace: false });
        e.preventDefault();
      } else onClick?.(e);
    },
    [onClick],
  );

  return (
    <ScrollLink
      className={classNames({ [styles.active]: isActive })}
      duration={scrollDuration}
      href={href}
      id={`${to}-link`}
      offset={navOffset}
      /* eslint-disable-next-line */
      // @ts-ignore
      onClick={(e) => handleClick(e, href)}
      onSetActive={onSetActive}
      smooth
      spy
      to={to}
    >
      {children}
    </ScrollLink>
  );
};

export const Nav = (): ReactElement => {
  const sierra = useSierra();
  const location = useLocation();
  const navigate = useNavigate();
  const [popToast] = useToast();
  const [profile, { error: profileError, isLoading: isLoadingProfile }] =
    useProfile();
  const [redirectToVendor, { isLoading: isRedirecting }] = useRedirectToVendor(
    profile?.id,
  );
  const { returnto } = useQueryParams();
  const { t } = useTranslation();
  const [active, setActive] = useState<string>('examRegistration');
  const [activeMenu, setActiveMenu] = useState<string | undefined>(undefined);
  const navRef = useRef<HTMLElement>(null);

  const links = [
    {
      id: 'homeView',
      label: t('Navigation.Link-Home'),
      route: '/home',
    },
    {
      id: 'certificationMenu',
      label: t('Navigation.Link-Cert'),
      onClick: (e: MouseEvent<HTMLElement>) => {
        setActiveMenu('certificationMenu');
      },
      menu: (
        <PopupMenu
          isActive={activeMenu === 'certificationMenu'}
          onClose={() => setActiveMenu(undefined)}
        >
          {[
            <PopupMenuItem
              key={t('Navigation.Link-MyExams') as string}
              label={t('Navigation.Link-MyExams')}
              onClick={() => performCertificationFeature('exams')}
            />,
            <PopupMenuItem
              key={t('Navigation.Link-Certifications') as string}
              label={t('Navigation.Link-Certifications')}
              onClick={() => performCertificationFeature('certifications')}
            />,
            <PopupMenuItem
              key={t('Navigation.Link-ContinuingEducation') as string}
              label={t('Navigation.Link-ContinuingEducation')}
              onClick={() => performCertificationFeature('continuingeducation')}
            />,
          ]}
        </PopupMenu>
      ),
    },
  ];

  useEffect(() => {
    if (location.pathname === '/')
      navigate(`/home${location.search}`, { replace: true });
    else if (
      location.pathname === '/training-products' ||
      location.pathname === '/profile' ||
      location.pathname === '/account'
    ) {
      setActive('');
    } else {
      const activeLink = (links as any).find((l: any) =>
        location.pathname.toLowerCase().startsWith(l.route ?? ''),
      );
      if (activeLink != null) setActive(activeLink.id);
    }
  });

  const performCertificationFeature = async (route: string) => {
    setActiveMenu(undefined);
    if (sierra.isEnabled()) sierra.navigateToSierraRoute(route);
    else {
      switch (route) {
        case 'exams':
          profile?.externalIds?.pearsonVue && env.FLAG_PEARSON_DIRECTSSO_ENABLED
            ? redirectToVendor(Vendors.PearsonVue)
            : navigate('/profile/pearson', { replace: true });
          break;
        case 'certifications':
        case 'continuingeducation':
          try {
            if (profile?.compTIAId) await redirectToVendor(Vendors.CertMetrics);
            else
              window.open('https://www.comptia.org/certifications', '_blank');
          } catch (error) {
            popToast((error as IError)?.message || t('Toast.Error.Default'));
          }
          break;
      }
    }
  };

  const handleSetActive = (id: string) => {
    setActive(id);
    const element = document.getElementById(`${id}-link`);
  };

  const getReturnTo = () => {
    if (returnto != null) {
      const { host } = new URL(returnto);
      console.log(host);
      if (redirectWhiteList.includes(host.toLowerCase())) {
        return returnto;
      }
    }
    return '/';
  };

  const MainRouteLinks = () => {
    useHostedRouterSync({ frameId: sierra.getFrameId() });
    return (
      <>
        {links.map((link) => (
          <li key={link.id}>
            <NavLink
              isActive={active === link.id}
              href={link.route}
              onClick={(e) => {
                // No longer needed
                link.onClick?.(e);
              }}
              onSetActive={handleSetActive}
              to={link.id}
            >
              {link.label}
            </NavLink>
            {activeMenu === link.id ? link.menu : <></>}
          </li>
        ))}
      </>
    );
  };

  return (
    <nav className={styles.nav} ref={navRef}>
      <ul>
        <Routes>
          <Route element={<MainRouteLinks />} path="/" />
          <Route element={<MainRouteLinks />} path="/certification/*" />
          <Route element={<MainRouteLinks />} path="/home" />
          <Route element={<MainRouteLinks />} path="/training" />
          <Route
            element={<MainRouteLinks />}
            path={`/${sierra.getFrameId()}/*`}
          />
          <Route
            element={
              <>
                <MainRouteLinks />
                <li className={styles.navLinkReturn}>
                  {getReturnTo() === '/' ? (
                    <a href={getReturnTo()}>
                      <ChevronLeftIcon />
                      {t('Navigation.Link-Return')}
                    </a>
                  ) : (
                    <a href={getReturnTo()}>
                      <ChevronLeftIcon />
                      {t('Navigation.Link-GoBack')}
                    </a>
                  )}
                </li>
              </>
            }
            path="*"
          />
        </Routes>
      </ul>
    </nav>
  );
};
